import { useState, useEffect } from 'react';
import { ArrowLeft, Download, Users, DollarSign, RefreshCw } from 'lucide-react';
import courseConfig from '../config/course';

const API_URL = courseConfig.apiUrl;

const styles = {
  page: {
    minHeight: '100vh',
    background: '#0a0a0a',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
  },
  header: {
    padding: '20px 24px',
    borderBottom: '1px solid #1f1f1f',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  backBtn: {
    background: 'none',
    border: '1px solid #333',
    borderRadius: '8px',
    color: '#a1a1aa',
    cursor: 'pointer',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    fontSize: '1.3rem',
    fontWeight: 700,
  },
  refreshBtn: {
    background: 'none',
    border: '1px solid #333',
    borderRadius: '8px',
    color: '#a1a1aa',
    cursor: 'pointer',
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
    padding: '24px',
    flexWrap: 'wrap',
  },
  statCard: {
    flex: '1 1 200px',
    background: '#141414',
    border: '1px solid #1f1f1f',
    borderRadius: '12px',
    padding: '20px',
  },
  statLabel: {
    fontSize: '0.8rem',
    color: '#71717a',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statValue: {
    fontSize: '1.8rem',
    fontWeight: 800,
    color: '#08F654',
  },
  tableContainer: {
    padding: '0 24px 24px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
    minWidth: '700px',
  },
  th: {
    textAlign: 'left',
    padding: '12px 16px',
    color: '#71717a',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    borderBottom: '1px solid #1f1f1f',
  },
  td: {
    padding: '14px 16px',
    borderBottom: '1px solid #141414',
    color: '#d4d4d8',
  },
  row: {
    transition: 'background 0.15s',
  },
  badge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    background: 'rgba(8,246,84,0.1)',
    color: '#08F654',
  },
  empty: {
    textAlign: 'center',
    padding: '80px 24px',
    color: '#71717a',
  },
  emptyTitle: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: '#a1a1aa',
    marginBottom: '8px',
  },
  loading: {
    textAlign: 'center',
    padding: '80px 24px',
    color: '#71717a',
  },
  error: {
    textAlign: 'center',
    padding: '40px 24px',
    color: '#ef4444',
  },
  exportBtn: {
    background: '#141414',
    border: '1px solid #333',
    borderRadius: '8px',
    color: '#a1a1aa',
    cursor: 'pointer',
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.85rem',
    fontFamily: 'inherit',
  },
  headerRight: {
    display: 'flex',
    gap: '8px',
  },
  loginPage: {
    minHeight: '100vh',
    background: '#0a0a0a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  loginCard: {
    background: '#141414',
    border: '1px solid #1f1f1f',
    borderRadius: '16px',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '380px',
    textAlign: 'center',
  },
  loginTitle: {
    fontSize: '1.3rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '8px',
  },
  loginSubtitle: {
    fontSize: '0.9rem',
    color: '#71717a',
    marginBottom: '24px',
  },
  loginInput: {
    width: '100%',
    padding: '12px 16px',
    fontSize: '1rem',
    border: '1px solid #333',
    borderRadius: '10px',
    outline: 'none',
    fontFamily: 'inherit',
    marginBottom: '16px',
    boxSizing: 'border-box',
    background: '#0a0a0a',
    color: '#ffffff',
  },
  loginBtn: {
    width: '100%',
    padding: '14px',
    fontSize: '1rem',
    fontWeight: 700,
    color: '#000000',
    background: '#08F654',
    border: 'none',
    borderRadius: '10px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  loginError: {
    color: '#ef4444',
    fontSize: '0.85rem',
    marginBottom: '12px',
  },
};

const ADMIN_PASSWORD = 'password';

function AdminDashboard({ onBack }) {
  const [authed, setAuthed] = useState(() => sessionStorage.getItem('admin_authed') === 'true');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPurchases = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/v1/admin/purchases?courseId=${courseConfig.courseId}`);
      const data = await res.json();
      if (data.success) {
        setPurchases(data.data.purchases || []);
      } else {
        setError(data.error?.message || 'Failed to load purchases');
      }
    } catch (err) {
      setError('Could not connect to the server');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (authed) fetchPurchases();
  }, [authed]);

  const totalRevenue = {};
  purchases.forEach((p) => {
    if (!totalRevenue[p.currency]) totalRevenue[p.currency] = 0;
    totalRevenue[p.currency] += p.amount;
  });

  const revenueString = Object.entries(totalRevenue)
    .map(([currency, amount]) => `${currency} ${amount.toLocaleString()}`)
    .join(', ') || '—';

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      sessionStorage.setItem('admin_authed', 'true');
      setAuthed(true);
      setLoginError('');
    } else {
      setLoginError('Incorrect password');
    }
  };

  if (!authed) {
    return (
      <div style={styles.loginPage}>
        <form style={styles.loginCard} onSubmit={handleLogin}>
          <h2 style={styles.loginTitle}>Admin Access</h2>
          <p style={styles.loginSubtitle}>Enter password to view purchases</p>
          {loginError && <p style={styles.loginError}>{loginError}</p>}
          <input
            style={styles.loginInput}
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          <button type="submit" style={styles.loginBtn}>Enter</button>
        </form>
      </div>
    );
  }

  const exportCSV = () => {
    if (purchases.length === 0) return;
    const headers = ['Name', 'Email', 'Amount', 'Currency', 'Status', 'Date'];
    const rows = purchases.map((p) => [
      p.name,
      p.email,
      p.amount,
      p.currency,
      p.status,
      new Date(p.createdAt).toLocaleString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `purchases-${courseConfig.courseId}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.backBtn} onClick={onBack}>
            <ArrowLeft size={18} />
          </button>
          <h1 style={styles.title}>Purchases</h1>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.exportBtn} onClick={exportCSV}>
            <Download size={16} /> Export CSV
          </button>
          <button style={styles.refreshBtn} onClick={fetchPurchases}>
            <RefreshCw size={16} /> Refresh
          </button>
        </div>
      </div>

      <div style={styles.statsRow}>
        <div style={styles.statCard}>
          <p style={styles.statLabel}><Users size={14} /> Total Purchases</p>
          <p style={styles.statValue}>{purchases.length}</p>
        </div>
        <div style={styles.statCard}>
          <p style={styles.statLabel}><DollarSign size={14} /> Revenue</p>
          <p style={{ ...styles.statValue, fontSize: '1.2rem' }}>{revenueString}</p>
        </div>
      </div>

      <div style={styles.tableContainer}>
        {loading && <p style={styles.loading}>Loading purchases...</p>}
        {error && <p style={styles.error}>{error}</p>}
        {!loading && !error && purchases.length === 0 && (
          <div style={styles.empty}>
            <p style={styles.emptyTitle}>No purchases yet</p>
            <p>Purchases will appear here once customers buy the product.</p>
          </div>
        )}
        {!loading && !error && purchases.length > 0 && (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>#</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Currency</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p, i) => (
                <tr
                  key={p.purchaseId || i}
                  style={styles.row}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#141414'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={styles.td}>{i + 1}</td>
                  <td style={{ ...styles.td, fontWeight: 600, color: '#ffffff' }}>{p.name}</td>
                  <td style={styles.td}>{p.email}</td>
                  <td style={{ ...styles.td, fontWeight: 600 }}>{p.amount.toLocaleString()}</td>
                  <td style={styles.td}>{p.currency}</td>
                  <td style={styles.td}>
                    <span style={styles.badge}>{p.status}</span>
                  </td>
                  <td style={styles.td}>{new Date(p.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
